/**
 * Copyright (c) 2026, CYBRA Corp.
 */
import { Component, OnInit, Input, ViewChild } from '@angular/core';

import {
  DataMappingService,
  DataMappingHeaderDto
} from 'app/services/data-mapping.service';

import { PagedResults } from 'app/models/paged-results';
import { RESULTS_PER_PAGE } from 'app/app-constants';

import {
  ERROR_DIALOG_TITLE,
  SimpleSuccessErrorDialogComponent
} from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

import { DataMappingViewDialogComponent } from './data-mapping-view.dialog.component';
import { DataMappingDtlDialogComponent } from './data-mapping-dtl.dialogs.component';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog.component';


type DataMappingHeaderUI = DataMappingHeaderDto & {
  selected: boolean;
};

@Component({
  selector: 'app-data-mapping-hdr',
  templateUrl: './data-mapping-hdr.component.html'
})
export class DataMappingHdrComponent implements OnInit {

  @ViewChild('datamappingTypeDtlDialog')
  dtlDialog!: DataMappingDtlDialogComponent;

  @ViewChild('viewDialog')
  viewDialog!: DataMappingViewDialogComponent;

  @ViewChild('confirmDeleteDialog')
  confirmDialog!: ConfirmDeleteDialogComponent;

  @ViewChild('successErrorDialog', { static: false })
  successErrorDialog!: SimpleSuccessErrorDialogComponent;

  @Input() displayHeader = true;

  /* ================= Paging ================= */

  mappedHeaders: DataMappingHeaderUI[] = [];

  totalNumberOfMappings = 0;
  currentPage = 1;

  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSize: number = this.resultsPerPageValues[0];

  filterString = '';

  constructor(private dataMappingService: DataMappingService) {}

  /* ================= Init ================= */

  ngOnInit(): void {
    this.refreshMappings();
  }

  /* ============================================================
   * SERVER-SIDE PAGING
   * ============================================================ */

  private async refreshMappings(): Promise<void> {

    let page: PagedResults<DataMappingHeaderDto>;

    try {
      page = await this.dataMappingService.getHeadersPaging(
        (this.currentPage - 1) * this.selectedPageSize,
        this.selectedPageSize,
        'ASC',
        this.filterString
      );
    } catch (error) {
      this.showError(error);
      return;
    }

    this.mappedHeaders = page.data.map(h => ({
      ...h,
      selected: false
    }));

    this.totalNumberOfMappings = page.size;
  }

  reloadHeaders(): void {
  this.refreshMappings();
}


  pageChanged(pageNumber: number): void {
    if (!pageNumber || pageNumber < 1) return;

    if (this.currentPage === pageNumber) return;

    this.currentPage = pageNumber;
    this.refreshMappings();
  }

  manageResultPerPageClicked(value: number): void {
    this.selectedPageSize = value;
    this.currentPage = 1;
    this.refreshMappings();
  }

  /* ============================================================
   * SELECTION
   * ============================================================ */

  isAnyDataMappingSelected(): boolean {
    return this.mappedHeaders.some(h => h.selected);
  }

  isSingleDataMappingSelected(): boolean {
    return this.mappedHeaders.filter(h => h.selected).length === 1;
  }

  isAllDataMappingSelected(): boolean {
    return (
      this.mappedHeaders.length > 0 &&
      this.mappedHeaders.every(h => h.selected)
    );
  }

  setAllDataMappingSelected(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.mappedHeaders.forEach(h => h.selected = checked);
  }

  /* ============================================================
   * ACTIONS
   * ============================================================ */

  addMappingDetail(): void {
    this.dtlDialog.showAdd();
  }

  editDataMappingClicked(): void {
    const selected = this.mappedHeaders.filter(h => h.selected);
    if (selected.length !== 1) return;

    this.dtlDialog.showEdit(selected[0]);
  }

  viewMappingClicked(header: DataMappingHeaderUI): void {
    this.viewDialog.show(header);
  }

  deleteDataMappingClicked(): void {
    if (!this.isAnyDataMappingSelected()) return;

    const names = this.getSelectedHeaderNames();
    this.confirmDialog.show('Delete Data Mapping', names);
  }

  async deleteConfirmed(): Promise<void> {
    const ids = this.getSelectedHeaderIds();
    if (!ids.length) return;

    try {
      await this.dataMappingService.deleteHeaders(ids);
      this.refreshMappings();
    } catch (error) {
      this.showError(error);
    }
  }

  /* ============================================================
   * HELPERS
   * ============================================================ */

  private getSelectedHeaderIds(): number[] {
    return this.mappedHeaders
      .filter(h => h.selected)
      .map(h => h.id!)
      .filter(Boolean);
  }

  private getSelectedHeaderNames(): string[] {
    return this.mappedHeaders
      .filter(h => h.selected)
      .map(h => h.mappingName);
  }

  private showError(error: any): void {
    this.successErrorDialog.showErrorDialog(
      ERROR_DIALOG_TITLE,
      error?.error || error?.message || error || 'Unexpected error occurred'
    );
  }

  
}
